import { useEffect, useState } from "react";
import { Link } from "wouter";
import { api, type AlertWithLead } from "@/lib/api-client";
import { Header } from "@/components/layout/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { formatTimeAgo } from "@/lib/utils";
import { AlertTriangle, BotOff, Clock, ArrowRight, Phone, CheckCircle, ShieldCheck } from "lucide-react";
import { useDashboardRealtime } from "@/lib/useDashboardRealtime";

export function AlertsPage() {
  const [alerts, setAlerts] = useState<AlertWithLead[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
  }, []);

  useDashboardRealtime({
    onAlertCreated: (payload) => {
      setAlerts((prev) => [{ alert: payload.alert, lead: payload.lead }, ...prev]);
    },
    onAlertAcked: (payload) => {
      setAlerts((prev) => prev.filter((a) => a.alert.id !== payload.alertId));
    },
  });

  const loadAlerts = async () => {
    try {
      const { alerts: openAlerts } = await api.getAlerts({ status: "open", limit: 100 });
      setAlerts(openAlerts);
    } catch (error) {
      console.error("Failed to load alerts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcknowledge = async (alertId: number) => {
    try {
      await api.ackAlert(alertId);
      setAlerts((prev) => prev.filter((a) => a.alert.id !== alertId));
    } catch (e) {
      console.error("Failed to acknowledge alert", e);
      alert(e instanceof Error ? e.message : "Failed to acknowledge alert");
    }
  };

  return (
    <>
      <Header 
        title="Alerts" 
        subtitle={`${alerts.length} alerts need attention`} 
      />

      <div className="p-6 space-y-6">
        {/* Summary Card */}
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-warning/10">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Human Intervention Required</h3>
                <p className="text-sm text-muted-foreground">
                  The agent flagged these conversations and requested a human interjection
                </p>
              </div>
              <div className="ml-auto">
                <Badge className="bg-warning text-warning-foreground text-lg px-4 py-1">
                  {alerts.length}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts List */}
        <div className="space-y-3">
          {isLoading ? (
            Array(3)
              .fill(0)
              .map((_, i) => (
                <Card key={i} className="border-border/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Skeleton className="w-12 h-12 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-28" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
          ) : alerts.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="All Clear!"
              description="No open alerts right now. The agent is handling everything smoothly."
              className="py-16 bg-success/5 rounded-2xl border border-dashed border-success/20"
            />
          ) : (
            alerts.map(({ alert: a, lead }) => (
              <Card
                key={a.id}
                className="border-warning/20 bg-warning/5 hover:border-warning/40 transition-all group shadow-sm"
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center shrink-0 mt-0.5">
                      <BotOff className="w-5 h-5 text-warning" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">
                          {lead?.name || lead?.phone || `Lead #${a.leadId}`}
                        </h3>
                        <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/20">
                          {a.reason}
                        </Badge>
                      </div>

                      {lead?.phone && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          <span>{lead.phone}</span>
                        </div>
                      )}

                      <div className="mt-3 space-y-1">
                        <div className="text-sm">
                          <span className="font-medium">Agent note:</span>{" "}
                          <span className="text-muted-foreground">{a.agentNote}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Last message:</span>{" "}
                          <span className="text-muted-foreground">“{a.incomingText}”</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimeAgo(a.createdAt)}
                      </span>

                      <div className="flex gap-2">
                        {lead?.id ? (
                          <Link href={`/chats/${lead.id}`}>
                            <Button size="sm" variant="outline">
                              View <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          </Link>
                        ) : null}
                        <Button size="sm" variant="default" onClick={() => handleAcknowledge(a.id)}>
                          Acknowledge
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </>
  );
}
